const OBFUSCATION_KEY = 0xA5;

function deobfuscate(encoded: string): string {
    try {
        const decoded = atob(encoded);
        let result = '';
        for (let i = 0; i < decoded.length; i++) {
            result += String.fromCharCode(decoded.charCodeAt(i) ^ OBFUSCATION_KEY);
        }
        return result;
    } catch (e) {
        // Fallback to the raw string if decoding/deobfuscation fails
        return encoded;
    }
}

let envCache: { [key: string]: string } | null = null;

function initializeEnv(): void {
    if (envCache) return;

    // Dynamically and synchronously fetch env-config.js if window._env_ is not set
    if (typeof window !== 'undefined' && typeof window.XMLHttpRequest !== 'undefined' && !(window as any)._env_) {
        try {
            const publicUrl = process.env.PUBLIC_URL || '';
            const xhr = new window.XMLHttpRequest();
            xhr.open('GET', `${publicUrl}/config/env-config.js`, false);
            xhr.send(null);
            if (xhr.status === 200) {
                try {
                    const script = document.createElement('script');
                    script.text = xhr.responseText;
                    (document.head || document.documentElement).appendChild(script);
                    if (script.parentNode) {
                        script.parentNode.removeChild(script);
                    }
                } catch (evalError) {
                    // Fallback to eval if script injection fails
                    (0, eval)(xhr.responseText);
                }
            }
        } catch (e) {
            console.error("Failed to load env-config.js dynamically", e);
        }
    }

    const defaults: { [key: string]: string } = {
        REACT_APP_ENVIRONMENT: "DEVELOPMENT",
        REACT_APP_PRIVATE_KEY: "DEV_KEY",
        REACT_APP_BACKEND_BASE_URL: "http://localhost:21000/api/",
        REACT_APP_KEYCLOAK_URL: "http://localhost:8080/",
        REACT_APP_KEYCLOAK_REALM: "ATMOS",
        REACT_APP_KEYCLOAK_RESOURCE_CLIENT: "ATMOS-RESOURCE-SERVER",
        REACT_APP_MAPBOX_TOKEN: "pk.eyJ1Ijoibml0ZXNoMjlnb3RoZSIsImEiOiJjbHI5ZG9jaGgwMmNmMmxxaWJjbmFpMWRxIn0.g4GWfpY1-Q0Lmm0-1hKNxg",
        REACT_APP_MAPBOX_STYLE_URL: 'mapbox://styles/nitesh29gothe/cm21te2t7001f01pd19sjbwx0',
        REACT_APP_COLLECT: "true",
        REACT_APP_AIRGAP: "false",
        REACT_APP_SECRET_KEY: "mV4O74ID8T86IwN6gAyrQPZ0UEEYb7Ku"
    };

    envCache = { ...defaults };

    if (typeof window !== 'undefined') {
        const rawEnv = (window as any)._env_;
        const rawToken = (window as any)._env_token_ || (window as any)._env_encoded_;

        let envObj: any = null;

        // 1. Check if token or encoded key is on window
        if (typeof rawToken === 'string' && rawToken.trim()) {
            try {
                const decrypted = deobfuscate(rawToken);
                envObj = JSON.parse(decrypted);
            } catch (e) {
                console.error("Failed to parse deobfuscated environment token", e);
            }
        }
        // 2. Check if _env_ itself is an obfuscated string
        else if (typeof rawEnv === 'string' && rawEnv.trim()) {
            try {
                const decrypted = deobfuscate(rawEnv);
                envObj = JSON.parse(decrypted);
            } catch (e) {
                console.error("Failed to parse deobfuscated window._env_ string", e);
            }
        }
        // 3. Check if _env_ is a plain object (development mode)
        else if (rawEnv && typeof rawEnv === 'object') {
            envObj = rawEnv;
        }

        // If we found variables, merge them into cache
        if (envObj && typeof envObj === 'object') {
            Object.keys(envObj).forEach((key) => {
                envCache![key] = envObj[key];
            });
        }

        // CRITICAL SECURITY FEATURE: Delete all global config references from window
        // to prevent users from inspecting them in Console or Developer Tools.
        try {
            delete (window as any)._env_;
            delete (window as any)._env_token_;
            delete (window as any)._env_encoded_;
        } catch (e) {
            // Fallback for strict modes or environments where delete fails
            (window as any)._env_ = undefined;
            (window as any)._env_token_ = undefined;
            (window as any)._env_encoded_ = undefined;
        }
    }
}

export function getEnvVar(varName: string): string {
    initializeEnv();
    let val = envCache && envCache[varName] !== undefined ? envCache[varName] : '';

    if (typeof window !== 'undefined' && typeof val === 'string' && val.startsWith('/')) {
        const host = window.location.host; // includes port if present (e.g. "localhost:3000")
        if (varName === 'REACT_APP_WEBSOCKET_BACKEND_BASE_URL') {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            val = `${protocol}//${host}${val}`;
        } else if (varName === 'REACT_APP_BACKEND_BASE_URL') {
            const protocol = window.location.protocol;
            val = `${protocol}//${host}${val}`;
        }
    }

    return val;
}

